import { Component, OnInit, OnDestroy, Optional } from "@angular/core";
import { FormBuilder, ValidatorFn, AbstractControl } from "@angular/forms";
import { Subscription, BehaviorSubject, Observable, from, of } from "rxjs";
import { tap, mergeMap, switchMap, map } from "rxjs/operators";

// Components
import { BaseControlComponent } from "../../../form-controls/components/base-control/base-control.component";

// Services
import { PaymentService, AuthorizationRequest, PaymentItemInformation, calculateTotal } from "../../services/payment.service";
import { PaymentCalculatorService, PaymentCalculatorConfigData } from "../../services/payment-calculator.service";
import { FlowButtonOverrideService } from "../../../flow/services/flow-button-override.service";
import { Button } from "../../../flow/models/flow.model";
import { PaymentItem, PaymentSearchCriteria, PaymentStatus, ApplicationPayment, Payment } from "../../models/paymentItem.model";

import { LoaderService } from "../../../common/services/loader.service";
import { FlowInstanceService } from "../../../flow/services/flow-instance.service";
import { AuthService } from "../../../services/auth.service";
import { genId } from "../../../core/services/genId";
import { ContextInteractionService } from "../../../form-controls/services/context-interaction.service";
import { SystemSettingsService } from "../../../admin/services/system-settings.service";
import { SystemSetting, SystemSettingSearchSpec, SettingNames } from "../../../admin/models/system-setting.model";



@Component({
    selector: "paymentCheckout",
    templateUrl: "./payment-checkout.component.html",
    providers: [FlowButtonOverrideService],
    standalone: false
})


export class PaymentCheckoutComponent extends BaseControlComponent implements OnInit, OnDestroy {

	static componentName = "PaymentCheckoutComponent";

	get allowExpediteFees(): boolean {
		return this.expediteSetting != null && this.expediteSetting.isActive;
	}

    authorizationRequest: AuthorizationRequest;


    get cartTotal(): string {
        if (!this.authorizationRequest || !this.authorizationRequest.paymentInformation) return "";
        return calculateTotal(this.authorizationRequest.paymentInformation.paymentItems);
    }

    get config(): any {
        return this.componentData.config;
    }

    set config(value: any) {
        this.componentData.config = value;
    }

    private get disableNext(): boolean {
        return (this.loadingPayments$.value === true || this.formInvalid() === true || this.paymentSubmitted === true);
    }

	emailAddress: string = "";

	expediteSetting: SystemSetting;

    isPreview: boolean = true;
    loadingPayments$ = new BehaviorSubject<boolean>(true);

	private _payments: PaymentItem[] = null;

    get payments(): PaymentItem[] {
		return this._payments;
    }

	set payments(value: PaymentItem[]) {
		this._payments = value;
	}

    get paymentAddress(): string {
        return this.config.paymentAddress;
    }

    paymentStepNumbers = Object.freeze({
        "itemList": 0,
        "paymentType": 1,
        "paymentTypeCheck": 2
    });

    paymentStepNumber: number = this.paymentStepNumbers.itemList;

    get paymentType(): string {
        const location = this.config.paymentType;
        return this.getContextData(location);
    }

    set paymentType(value: string) {
        // For some reason this is firing a null value and wiping out stored payment type in review forms
        if (this.isInReview) {
            value = this.paymentType;
        }

        const location = this.config.paymentType;
        this.saveContextData(location, value);
        this.authorizationRequest.accountInformation.paymentType = value;
        this.paymentService.updateCurrentAuthorizationRequest(this.authorizationRequest);
    }

	paymentSubmitted: boolean = false;

	settingsObservable: Observable<SystemSetting>;

    subscriptions: Subscription = new Subscription();

    workflowId: string = "";

    constructor(
        public readonly authService: AuthService,
        public readonly paymentService: PaymentService,
        public readonly paymentCalculatorService: PaymentCalculatorService,
        @Optional() public readonly flowOverrideService: FlowButtonOverrideService,
        public readonly loaderService: LoaderService,
        public readonly flowService: FlowInstanceService,
        private builder: FormBuilder,
		public readonly contextService: ContextInteractionService,
		private readonly _settings: SystemSettingsService
    ) {
        super(contextService);
    }

    ngOnDestroy() {
        if (this.flowOverrideService)
            this.flowOverrideService.resetOverride();
        if (this.subscriptions)
            this.subscriptions.unsubscribe();
    }

    async ngOnInit() {
        if (!this.componentData.contextTarget) {
            this.componentData.contextTarget = "$.data.paymentCheckout";
        }
        if (!this.config.paymentType) {
            this.config = {
                amountDescription: "",
                amountDue: "",
                enableEditing: false,
                paymentAddress: "",
                paymentType: "$.data.paymentType.value",
                permitNewOrExisting: "$.data.newOrExisting.value",
                permitUrbanArea: "$.data.urbanArea.value",
                permitWellType: "$.data.WellType.value",
                permitWellTypeDescription: "$.data.WellTypeDescription.value",
                permitFluidProposed: "$.data.fluidProposed.value",
                permitPropDIJVmax: "$.data.propDIJVmax.value",
                permitUnitMPn: "$.data.unitMPn.value",
                permitExpedite: "$.data.expeditePermit.value",
                newOpsPermitType: "$.data.permitType.value",
                usePermitFees: true
            };
		}

		let spec: SystemSettingSearchSpec = {
			settingName: SettingNames.AllowExpedite
		} as SystemSettingSearchSpec;

		this.settingsObservable = this._settings.search(spec).pipe(
			map((settings: SystemSetting[]) => {
				return settings.find(s => s.settingName === SettingNames.AllowExpedite);
			}),
			tap((setting: SystemSetting) => {
				this.expediteSetting = setting;
			})
		)

        super.ngOnInit();

        if (this.flowOverrideService) {
            this.flowOverrideService.flow = this.flowComponent;
            this.flowOverrideService.incrementFunction = this._incrementFunction.bind(this);
            this.flowOverrideService.setValidatorNextButton(() => { return this.disableNext; });
            this.flowOverrideService.decrementFunction = this._decrementFunction.bind(this);           
        }

        this.subscriptions.add(this.enabledObs$.subscribe((isEnabled: boolean) => {
            this.onEnabled(isEnabled);
        },
            (error) => { console.error(error) }));
    }

    onEnabled(enabled: boolean) {
        if (!this.isEdit && this.isVisible) {
            this.workflowId = this.flowComponent.workflow.id;

           

            if (!this.authorizationRequest) {
                this.authorizationRequest = this.getAuthorizationRequest();

            }

            // Get data from context
            if (this.isEnabled && this.enableEditing) {
                if (this.config.usePermitFees) {
                    this.loaderService.toggleLoader(true);
                    // Gather all items and their fees
                    const paymentData = {
                        "newOrExisting": this.getContextData(this.config.permitNewOrExisting),
                        "urbanArea": this.getContextData(this.config.permitUrbanArea),
                        "WellType": this.getContextData(this.config.permitWellType),
                        "WellTypeDescription": this.getContextData(this.config.permitWellTypeDescription),
                        "fluidProposed": this.getContextData(this.config.permitFluidProposed),
                        "propDIJVmax": this.getContextData(this.config.permitPropDIJVmax),
                        "unitMPn": this.getContextData(this.config.permitUnitMPn),
                        "expeditePermit": this.getContextData(this.config.permitExpedite),
                        "wellTransferAmount": this.getContextData(this.config.wellTransferAmount),
                        "datePriortoWhichFeeIsAssessed": this.getContextDataOrReturnTargetAsValue(this.config.datePriortoWhichFeeIsAssessed),
                        "wellTransferSaleDate": this.getContextData(this.config.wellTransferSaleDate),
                        "BNDcashInLieuAmount": this.getContextData(this.config.BNDcashInLieuAmount),
                        "BNDoneTimeFeeAmount": this.getContextData(this.config.BNDoneTimeFeeAmount),
                        "newOpsPermitType": this.getContextData(this.config.newOpsPermitType)
                    } as PaymentCalculatorConfigData;

                    if (this.config.suretyTypeField != null) paymentData.suretyTypeField = this.getContextData(this.config.suretyTypeField)
                    else paymentData.suretyTypeField = [];

					this.settingsObservable.toPromise().then(
						(setting) => {
							this.paymentCalculatorService.calculate(paymentData, this.workflowId, this.flowComponent.workflow.templateId, this.allowExpediteFees).toPromise().then((calculations) => {
								if (this.authorizationRequest.paymentInformation.paymentItems.length === calculations.length) {
									let counter = 0;
									let foundAll = true;
									while (counter < calculations.length && foundAll) {
										let found: boolean = true;
										for (let i = 0; i < this.authorizationRequest.paymentInformation.paymentItems.length; i++) {
											const payment = this.authorizationRequest.paymentInformation.paymentItems[i];
											found = (payment.accountDescription === calculations[counter].accountDescription && payment.fee === calculations[counter].fee);
											if (found) break;
										}
										foundAll = found;
										counter = counter + 1;
									}
									if (!foundAll) {
										this.paymentService.deletePaymentsForApplication(this.authorizationRequest).toPromise().then((result) => {
											this.setPayments(calculations);
											this.loaderService.toggleLoader(false);
										}, (error) => {
											console.log(error.error);
											this.loadingPayments$.next(false);
											this.loaderService.toggleLoader(false);
										});
									} else {
										this.setPayments(calculations);
										this.loadingPayments$.next(false);
										this.loaderService.toggleLoader(false);
									}
								} else {
									if (!this.isInReview) {
										this.paymentService.deletePaymentsForApplication(this.authorizationRequest).toPromise().then((response) => {
											this.setPayments(calculations);
											this.loadingPayments$.next(false);
											this.loaderService.toggleLoader(false);
										}, (error) => {
											console.log(error.error);
											this.loadingPayments$.next(false);
											this.loaderService.toggleLoader(false);
										});
									} else {
										if (this.authorizationRequest.paymentInformation.paymentItems.length === 0) {
											this.authorizationRequest.paymentInformation.addItems([this.buildPaymentItem()]);
										}
										this.setPayments(calculations);
										this.loadingPayments$.next(false);
										this.loaderService.toggleLoader(false);
									}
								}
							}, (error) => {
								console.log(`ERROR in calculate: ${error.error}`);
								this.loadingPayments$.next(false);
								this.loaderService.toggleLoader(false);
							});
						}
					)

					
                } else {
					if (this.authorizationRequest.paymentInformation.paymentItems.length === 0) {
						//this.authorizationRequest.paymentInformation.clearPurchases();
						//this.authorizationRequest.paymentInformation.addItems([this.buildPaymentItem()]);
						this.setPayments([this.buildPaymentItem()]);
					} else {
						this.paymentService.updateCurrentAuthorizationRequest(this.authorizationRequest);
						this.payments = this.authorizationRequest.paymentInformation.paymentItems;
					}                    
                    this.loadingPayments$.next(false);
                    this.storeRequestInContext();
                }
            }
            else {
                this.loadingPayments$.next(false);
            }
            
        }
    }

    buildPaymentItem(): PaymentItem {
        let storedFee = this.getContextData(this.config.amountDue);
        let fee = 0.00;
        if (isNaN(storedFee)) {
            storedFee = storedFee.replace("$", "").replace(",", ""); //strip off possible currency formatting in case that's the problem
            if (isNaN(Number(storedFee))) {
                fee = 0.00;
            } else {
                fee = (storedFee.length > 0) ? Number(storedFee) : 0.00;
            }
        } else {
            fee = Number(storedFee);
        }

        const description = this.config.amountDescription;
        const payment = new PaymentItem("", description, "", fee, "", "", "", "", "", "", description, this.workflowId);
        return payment;
    }

    formInvalid(): boolean {
        if (this.formGroup === undefined || this.formGroup === null) return false;
        return this.formGroup.invalid;
    }

    getAuthorizationRequest(): AuthorizationRequest {
        const saved = this.getContext("payments");
        let authorization: AuthorizationRequest;
        if (saved) {
            var tmpAuth = typeof (saved) === "string" ? JSON.parse(saved) : saved;
            authorization = new AuthorizationRequest(tmpAuth.accountInformation, tmpAuth.billingAddress, tmpAuth.paymentInformation);
            authorization.applicationNumber = tmpAuth.applicationNumber;
            authorization.applicationType = tmpAuth.applicationType;
            authorization.cancelUrl = tmpAuth.cancelUrl;
            authorization.failUrl = tmpAuth.failUrl;
            authorization.flowUrl = tmpAuth.flowUrl;
            authorization.successUrl = tmpAuth.successUrl;
            return authorization;
        } else {
            authorization = new AuthorizationRequest(null, null, null);
            authorization.accountInformation.paymentType = this.paymentType;
            authorization.applicationType = this.flowComponent.workflow.name;
            if (this.config.applicationNumber) {
                const applicationNumber = this.getContextData(this.config.applicationNumber);
                if (applicationNumber !== undefined && applicationNumber !== null) {
                    authorization.applicationNumber = applicationNumber;
                }
            }
            if (!authorization.applicationNumber) {
                authorization.applicationNumber = genId();
            }
            this.paymentService.updateCurrentAuthorizationRequest(this.authorizationRequest);
        }
        return authorization;
    }

    paymentTypeChanged(e: Event): void {
        this.paymentType = this.formGroup.controls["paymentType"].value;
        this.formGroup.controls["paymentType"].updateValueAndValidity();
    }

    removeItem(paymentItem: PaymentItemInformation): void {
        this.authorizationRequest.paymentInformation.removeItem(paymentItem);
    }

    setPayments(payments: PaymentItem[]): void {
        this.authorizationRequest.paymentInformation.clearPurchases();
        this.authorizationRequest.paymentInformation.addItems(payments);
		this.paymentService.updateCurrentAuthorizationRequest(this.authorizationRequest);
		this.payments = payments;
    }


	protected _incrementFunction(button: Button): void {
		if (this.payments != null && this.payments.length > 0) {
			if (this.paymentStepNumber === this.paymentStepNumbers.paymentType) {
				if (this.enableEditing && this.isEnabled) {
					if (!this.paymentSubmitted) {
						this.paymentSubmitted = true;
						this.loaderService.toggleLoader(true);
						let nextObs$ = this.paymentService.savePaymentRequest(this.flowComponent.workflow.organizationId, this.authorizationRequest).pipe(
							mergeMap((authorization: AuthorizationRequest) => {
								console.log("savePaymentRequest succeeded");
								this.authorizationRequest = authorization;
								this.paymentService.updateCurrentAuthorizationRequest(this.authorizationRequest);
								return this.storeRequestInContext();
							}),
							mergeMap(() => {
								console.log("store request in context succeeded");
								const criteria = new PaymentSearchCriteria(this.authService.User.organizationId, this.authorizationRequest.applicationNumber, [PaymentStatus.Paid]);
								return this.paymentService.search(criteria);
							}),
							mergeMap((results: ApplicationPayment[]) => {
								console.log("payment search succeeded");
								for (let r = 0; r < results.length; r++) {
									const index = this.authorizationRequest.paymentInformation.paymentItems.findIndex(p => p.id === results[r].id);
									if (index !== -1) {
										this.authorizationRequest.paymentInformation.paymentItems.splice(index, 1);
									}
								}
								let obs$: Observable<string> = of(null);
								if (this.authorizationRequest.paymentInformation.paymentItems.length > 0 && this.authorizationRequest.accountInformation.paymentType.toLowerCase() !== "localcheck") {
									this.paymentService.updateCurrentAuthorizationRequest(this.authorizationRequest);
									obs$ = from(this.flowComponent.workflow.saveContext()).pipe(
										tap(() => { console.log("context updated"); }),
										switchMap(() => {
											this.setRedirects();
											return this.paymentService.redirectToCBoss();
										})
									)
								}
								return obs$;
							})
						).subscribe(
							(destination: any) => {
								this.loaderService.toggleLoader(false);
								if (destination != null) {
									window.location.href = destination;
								} else {
									this.flowOverrideService.callOriginalFunction(button);
								}
							},
							(error) => {
								console.error(error);
							}
						);
						this.subscriptions.add(nextObs$);
					}
				} else {
					this.flowOverrideService.callOriginalFunction(button);
				}
			} else {
				this.paymentStepNumber++;
				this.formGroup = this.builder.group({
					paymentType: [{ value: this.paymentType, disabled: (!this.enableEditing && !this.isEnabled) }, [this.formValidator()]]
				});
			}
		}
		else {
			this.flowOverrideService.callOriginalFunction(button);
		}
    }

    protected _decrementFunction(button: Button): void {
        if (this.paymentStepNumber === this.paymentStepNumbers.itemList) {
            this.flowOverrideService.callOriginalFunction(button);
        } else {
            if (this.paymentStepNumber > 0)
                this.paymentStepNumber--;
            this.formGroup = this.builder.group({});
        }
    }

    protected _disabledIncrementFunction(button: Button): void {
        if (this.paymentStepNumber === this.paymentStepNumbers.paymentType) {
            this.flowOverrideService.callOriginalFunction(button);
        }
        else {
            this.paymentStepNumber++;
            this.formGroup = this.builder.group({
                paymentType: [{ value: this.paymentType, disabled: true }, []]
            });
        }

    }


    public saveContext() {
        if (this.enableEditing) {
              this.flowComponent.promiseSaveContext()
                .then(() => {
                    this.setRedirects();
                    this.paymentService.redirectToCBoss().subscribe(response => {
                        window.location.href = response;
                    });
                });
        }
    }

    public setRedirects() {
        const id = this.flowComponent.workflow.id;
        const successSection = this.config.responseSection;
        const successPage = this.config.responsePage;
        const flowPrefix = (this.flowComponent.workflow.onBehalf) ? 'flow/on-behalf' : 'flow';
        this.authorizationRequest.successUrl = `/${flowPrefix}/${id}/${successSection}/${successPage}`;
        this.authorizationRequest.cancelUrl = `/${flowPrefix}/${id}/${this.flowComponent.currentSectionIndex}/${this.flowComponent.currentPageIndex}`;
        this.authorizationRequest.failUrl = `/${flowPrefix}/${id}/${successSection}/${successPage}`;
        this.paymentService.updateCurrentAuthorizationRequest(this.authorizationRequest);
    }

    public storeRequestInContext(): Observable<any> {
        let obs$: Observable<any> = of(null);
        if (this.enableEditing && this.isEnabled) {
            this.storeContext(JSON.stringify(this.authorizationRequest), "payments");
            this.updateChangeRules();

            obs$ = from(this.flowComponent.workflow.saveContext()).pipe(
                tap(() => { console.log("context updated"); })
            );
        }
        return obs$;
    }

    formValidator(): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } | null => {
            let msg = "";
            if (this.paymentStepNumber !== this.paymentStepNumbers.itemList && (this.paymentType === undefined || this.paymentType === null || this.paymentType === "")) {
                msg = "Payment Type is Required";
            }
            return (msg.length > 0) ? { "paymentType": { value: msg } } : null;
        };
    }
}